/**
 * Who opened an announcement, and undoing that.
 *
 * Removing someone's record is a real delete on the BFF, because "visto" is the *absence* of a
 * row — the announcement is delivered again on that person's next visit. It is behind
 * `whats_new:reset_viewer`, which only super admins hold.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTModal } from '@group-link-one/gl-ui/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { getWhatsNewViewers, resetWhatsNewViewer } from '../../Services/WhatsNew.service';
import type { WhatsNew } from '../../Types/WhatsNew.types';
import { useActionToast } from '@/Hooks/useActionToast';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { IconButton } from '@/Shared/components/IconButton/IconButton';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  width: 100%;
  min-width: 0;
`;

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
`;

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
`;

const idStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const metaStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

const noteStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

/** `7400` → `7,4 s`. Reading time is reported per person, in milliseconds. */
function formatDuration(durationMs: number | null): string {
  if (durationMs === null) return 'sem tempo registrado';
  return `${(durationMs / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
}

function formatSeenAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export interface WhatsNewViewersModalProps {
  open: boolean;
  item: WhatsNew;
  canReset: boolean;
  onClose: () => void;
}

export const WhatsNewViewersModal = ({
  open,
  item,
  canReset,
  onClose,
}: WhatsNewViewersModalProps): JSX.Element => {
  const toast = useActionToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['whats-new', item.id, 'viewers'],
    queryFn: () => getWhatsNewViewers(item.id),
    enabled: open,
  });

  const reset = useMutation({
    mutationFn: (platformUserId: number) => resetWhatsNewViewer(item.id, platformUserId),
    onSuccess: (_result, platformUserId) => {
      void queryClient.invalidateQueries({ queryKey: ['whats-new', item.id, 'viewers'] });
      // The card's reach count comes from the listing, which is now one viewer out of date.
      void queryClient.invalidateQueries({ queryKey: ['whats-new', 'list'] });
      toast.success(
        `Usuário ${platformUserId} vai ver de novo`,
        'O anúncio volta a aparecer para essa pessoa no próximo acesso.',
      );
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível reexibir o anúncio', error);
    },
  });

  const viewers = data?.viewers ?? [];

  const content = (
    <div className={bodyStyle}>
      {isLoading ? (
        <UTLoading type='dots' />
      ) : viewers.length === 0 ? (
        <EmptyState
          id='whats-new-viewers-empty'
          icon='Misc.Eye'
          title='Ninguém viu ainda'
          description='Assim que alguém abrir o anúncio na plataforma, aparece aqui.'
        />
      ) : (
        <div className={listStyle}>
          {viewers.map(viewer => (
            <div key={viewer.platformUserId} className={rowStyle}>
              <div>
                <div className={idStyle}>#{viewer.platformUserId}</div>
                <div className={metaStyle}>
                  {formatSeenAt(viewer.seenAt)} · {formatDuration(viewer.durationMs)}
                  {viewer.viewCount > 1 ? ` · abriu ${viewer.viewCount}×` : ''}
                </div>
              </div>
              <div className={spacerStyle} />
              {canReset ? (
                <IconButton
                  id={`whats-new-viewer-reset-${viewer.platformUserId}`}
                  icon='Arrows.ArrowCounterClockwise'
                  size='sm'
                  disabled={reset.isPending}
                  aria-label={`Fazer o usuário ${viewer.platformUserId} ver de novo`}
                  onClick={() => {
                    reset.mutate(viewer.platformUserId);
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}

      <p className={noteStyle}>
        São os identificadores dos usuários da plataforma — nome e e-mail ficam no backend de
        contas, não aqui.
        {canReset
          ? ' Reexibir apaga o registro de que a pessoa viu, e o anúncio volta a aparecer para ela.'
          : ''}
      </p>

      <div className={footerStyle}>
        <UTButton fitContent text='Fechar' onClick={onClose} />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title={`Quem viu · ${data?.total ?? viewers.length}`}
      content={content}
      maxWidth='560px'
      hasButtonClose
      position='center'
    />
  );
};
